/** @jsxImportSource @emotion/react */
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import PaymentIcon from "@mui/icons-material/Payment";
import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { formatCurrency } from "../utils";
import t from "../../../theme";

const styles = {
  form: {
    display: "grid",
    gap: 14,
  },
  title: {
    margin: 0,
    color: "#101828",
    fontSize: "0.95rem",
    fontWeight: 800,
  },
  helper: {
    margin: 0,
    color: "#667085",
    fontSize: "0.78rem",
    lineHeight: 1.5,
  },
  summary: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "12px 14px",
    borderRadius: 12,
    border: `1px solid ${t.green.border}`,
    background: t.green.bg,
  },
  summaryLabel: {
    display: "grid",
    gap: 2,
    color: "#667085",
    fontSize: "0.72rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  summaryCampaign: {
    color: "#101828",
    fontSize: "0.8rem",
    fontWeight: 700,
    textTransform: "none",
    letterSpacing: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: 220,
  },
  summaryValue: {
    color: t.green.dark,
    fontSize: "1.35rem",
    fontWeight: 800,
    whiteSpace: "nowrap",
  },
  button: {
    borderRadius: 999,
    textTransform: "none",
    fontWeight: 800,
    minHeight: 44,
    background: t.green.gradient,
    color: t.brand.white,
    boxShadow: t.shadow.brand,
    "&:hover": {
      background: "linear-gradient(135deg, #4a9b2e 0%, #2d7a15 100%)",
      boxShadow: t.shadow.brandFocus,
    },
    "&.Mui-disabled": {
      background: t.glass.subtle,
      color: t.text.muted,
    },
  },
};

const CampaignPaymentForm = ({
  onSuccess,
  amount,
  campaignTitle,
  onProcessingChange,
  mode = "payment",
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasAmount = Number.isFinite(Number(amount));
  const formattedAmount = hasAmount ? formatCurrency(amount) : "";
  // Free campaigns run a SetupIntent — the card is verified, never charged —
  // so it has to be confirmed with confirmSetup rather than confirmPayment.
  const isSetupMode = mode === "setup";

  // Let the parent keep the payment modal open while Stripe is confirming —
  // unmounting mid-confirmation would lose the success callback.
  useEffect(() => {
    if (onProcessingChange) onProcessingChange(isSubmitting);
  }, [isSubmitting, onProcessingChange]);

  const submitPayment = async (event) => {
    event.preventDefault();
    if (!stripe || !elements || isSubmitting) return;

    setIsSubmitting(true);
    const { error: submitError } = await elements.submit();
    if (submitError) {
      toast.error(submitError.message || "Please check your payment details.");
      setIsSubmitting(false);
      return;
    }

    const confirmParams = {
      return_url: `${window.location.origin}/my-bookings`,
    };

    const { error, paymentIntent, setupIntent } = isSetupMode
      ? await stripe.confirmSetup({
          elements,
          confirmParams,
          redirect: "if_required",
        })
      : await stripe.confirmPayment({
          elements,
          confirmParams,
          redirect: "if_required",
        });

    if (error) {
      toast.error(
        error.message ||
          (isSetupMode
            ? "We could not verify that card. Please try another."
            : "Payment failed. Please try again."),
      );
      setIsSubmitting(false);
      return;
    }

    const intent = isSetupMode ? setupIntent : paymentIntent;

    if (intent?.status === "succeeded") {
      toast.success(
        isSetupMode ? "Card verified — ticket booked." : "Ticket payment successful.",
      );
      if (onSuccess) onSuccess(intent);
      return;
    }

    if (intent?.status === "processing") {
      toast.info(isSetupMode ? "Card verification is processing." : "Payment is processing.");
      setIsSubmitting(false);
      return;
    }

    toast.warning(
      isSetupMode
        ? "Card verification was not completed. Please try again."
        : "Payment was not completed. Please try again.",
    );
    setIsSubmitting(false);
  };

  return (
    <form css={styles.form} onSubmit={submitPayment}>
      <div>
        <h3 css={styles.title}>Complete ticket payment</h3>
        <p css={styles.helper}>
          {isSetupMode
            ? "This campaign is free. We verify your card to confirm you are a genuine booker — nothing is charged."
            : "Enter your card details before the payment window closes."}
        </p>
      </div>
      {hasAmount && (
        <div css={styles.summary}>
          <span css={styles.summaryLabel}>
            {isSetupMode ? "You will be charged" : "You are paying"}
            {campaignTitle && (
              <span css={styles.summaryCampaign} title={campaignTitle}>
                {campaignTitle}
              </span>
            )}
          </span>
          <span css={styles.summaryValue}>{formattedAmount}</span>
        </div>
      )}
      <PaymentElement />
      <Button
        css={styles.button}
        type="submit"
        variant="contained"
        disabled={!stripe || !elements || isSubmitting}
        startIcon={isSubmitting ? <CircularProgress size={16} /> : <PaymentIcon />}
      >
        {isSubmitting
          ? "Processing..."
          : hasAmount
            ? `Pay ${formattedAmount}`
            : "Pay Now"}
      </Button>
    </form>
  );
};

export default CampaignPaymentForm;
